import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';
import { Calendar as CalendarIcon, User as UserIcon, Banknote, ExternalLink, Trash, ClipboardList as RegistroLogoIcon, FileDown } from 'lucide-react';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function mapById(list) {
  const map = {};
  list.forEach((item) => {
    if (item && typeof item.id !== 'undefined') {
      map[item.id] = item;
    }
  });
  return map;
}

function normalizeDateTimeLocal(value) {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) return null;
  return `${datePart} ${timePart}:00`;
}

export default function AttivitaRegistro() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addJob } = useJob();
  const isAdmin = (user?.role || user?.role_name || '').toLowerCase() === 'admin';

  const [operationDate, setOperationDate] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });
  const [autoUpdateNow, setAutoUpdateNow] = useState(true);
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [issuerOptions, setIssuerOptions] = useState([]);
  const [loadingIssuer, setLoadingIssuer] = useState(false);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [issuerQuery, setIssuerQuery] = useState('');
  const [issuerOpen, setIssuerOpen] = useState(false);

  const [statoInizialeList, setStatoInizialeList] = useState([]);
  const [attivitaSvoltaList, setAttivitaSvoltaList] = useState([]);
  const [statoFinaleList, setStatoFinaleList] = useState([]);
  const [statoInizialeId, setStatoInizialeId] = useState('');
  const [attivitaSvoltaId, setAttivitaSvoltaId] = useState('');
  const [statoFinaleId, setStatoFinaleId] = useState('');

  const [nota, setNota] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportUserId, setReportUserId] = useState('');
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingRows, setLoadingRows] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customerDetailsMap, setCustomerDetailsMap] = useState({});
  const [issuerDetailsMap, setIssuerDetailsMap] = useState({});
  const [filterStatoInizialeId, setFilterStatoInizialeId] = useState('');
  const [filterAttivitaSvoltaId, setFilterAttivitaSvoltaId] = useState('');
  const [filterStatoFinaleId, setFilterStatoFinaleId] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const statoInizialeMap = mapById(statoInizialeList);
  const attivitaSvoltaMap = mapById(attivitaSvoltaList);
  const statoFinaleMap = mapById(statoFinaleList);

  const normalizeIsoDateOnly = (value) => {
    if (!value) return '';
    const s = String(value);
    if (s.includes('T')) return s.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    return '';
  };

  const handleOpenReportDialog = () => {
    const dateOnly = normalizeIsoDateOnly(operationDate) || new Date().toISOString().split('T')[0];
    setReportDateFrom(dateOnly);
    setReportDateTo(dateOnly);
    setReportUserId(filterUserId || '');
    setReportDialogOpen(true);
    fetchUserOptions('');
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
  };

  const handleConfirmGenerateReportPdf = async () => {
    const from = normalizeIsoDateOnly(reportDateFrom);
    const to = normalizeIsoDateOnly(reportDateTo);
    const userId = String(reportUserId || '').trim();

    if (!userId) {
      toast.error('Seleziona un utente');
      return;
    }
    if (!from || !to) {
      toast.error('Seleziona un intervallo di date valido');
      return;
    }
    if (from > to) {
      toast.error('La data "Da" non può essere maggiore della data "A"');
      return;
    }

    const selectedUser = userOptions.find((u) => String(u.id) === String(userId)) || null;
    const selectedUserLabel = selectedUser
      ? ([selectedUser.name, selectedUser.surname].filter(Boolean).join(' ') || selectedUser.email || `ID: ${selectedUser.id}`)
      : `ID: ${userId}`;

    setGeneratingReport(true);
    try {
      const response = await api.post('/attivita/registro/report', {
        userId: Number(userId),
        dateFrom: from,
        dateTo: to,
      });
      const jobId = response?.data?.jobId;
      if (!jobId) {
        toast.error('Risposta non valida dal server');
        return;
      }
      addJob(jobId, 'admin_activity_summary', {
        meta: {
          kind: 'attivita_registro_pdf',
          userLabel: selectedUserLabel,
          dateFrom: from,
          dateTo: to,
          dateFromIt: formatIsoDateToIt(from),
          dateToIt: formatIsoDateToIt(to),
        }
      });
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Error requesting PDF:', error);
      toast.error('Errore durante la richiesta del PDF');
    } finally {
      setGeneratingReport(false);
    }
  };

  const fetchLookups = async () => {
    try {
      setLoadingIssuer(true);
      const [statoInizialeRes, attivitaSvoltaRes, statoFinaleRes, issuerRes] = await Promise.all([
        api.get('/attivita/stato-iniziale'),
        api.get('/attivita/attivita-svolta'),
        api.get('/attivita/stato-finale'),
        api.get('/issuer'),
      ]);
      setStatoInizialeList(statoInizialeRes.data || []);
      setAttivitaSvoltaList(attivitaSvoltaRes.data || []);
      setStatoFinaleList(statoFinaleRes.data || []);
      setIssuerOptions(issuerRes.data || []);
    } catch (error) {
      console.error('Error fetching lookup data for Registro Attività:', error);
      toast.error('Errore nel caricamento delle tabelle di supporto');
    } finally {
      setLoadingIssuer(false);
    }
  };

  const fetchCustomerOptions = async (query) => {
    setLoadingCustomer(true);
    try {
      const params = {
        page: 1,
        limit: 10,
        orderBy: 'id',
        order: 'desc',
      };
      if (query) params.search = query;
      const res = await api.get('/customer', { params });
      const data = res.data && Array.isArray(res.data.data) ? res.data.data : [];
      setCustomerOptions(data);
    } catch (error) {
      console.error('Error fetching customers for Registro Attività:', error);
      toast.error('Errore nel caricamento clienti');
    } finally {
      setLoadingCustomer(false);
    }
  };

  const fetchIssuerOptions = async (query) => {
    setLoadingIssuer(true);
    try {
      const params = {};
      if (query) params.q = query;
      const res = await api.get('/issuer', { params });
      const data = Array.isArray(res.data) ? res.data : [];
      setIssuerOptions(data);
    } catch (error) {
      console.error('Error fetching issuers for Registro Attività:', error);
      toast.error('Errore nel caricamento banche/finanziarie');
    } finally {
      setLoadingIssuer(false);
    }
  };

  const currentInputDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (!autoUpdateNow) return;
    const tick = () => {
      setOperationDate(currentInputDate());
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [autoUpdateNow]);

  const fetchUserOptions = async (query) => {
    if (!isAdmin) return;
    setLoadingUsers(true);
    try {
      const params = {};
      if (query) params.search = query;
      const res = await api.get('/attivita/registro/users', { params });
      const list = Array.isArray(res.data) ? res.data : [];
      setUserOptions(list);
    } catch (error) {
      console.error('Error fetching users for Registro Attività:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const hydrateDetails = async (rowsData) => {
    const customerIds = Array.from(
      new Set(
        rowsData
          .map((row) => row.customer_id)
          .filter((id) => id !== null && typeof id !== 'undefined')
      )
    );
    const issuerIds = Array.from(
      new Set(
        rowsData
          .map((row) => row.issuer_id)
          .filter((id) => id !== null && typeof id !== 'undefined')
      )
    );

    const customerIdsToFetch = customerIds.filter((id) => !customerDetailsMap[id]);
    const issuerIdsToFetch = issuerIds.filter((id) => !issuerDetailsMap[id]);

    if (customerIdsToFetch.length === 0 && issuerIdsToFetch.length === 0) {
      return;
    }

    try {
      const [customerResults, issuerResults] = await Promise.all([
        Promise.all(
          customerIdsToFetch.map((id) =>
            api
              .get(`/customer/${id}`)
              .then((res) => ({ id, data: res.data }))
              .catch((error) => {
                console.error('Error fetching customer for Registro Attività:', error);
                return null;
              })
          )
        ),
        Promise.all(
          issuerIdsToFetch.map((id) =>
            api
              .get(`/issuer/${id}`)
              .then((res) => ({ id, data: res.data }))
              .catch((error) => {
                console.error('Error fetching issuer for Registro Attività:', error);
                return null;
              })
          )
        ),
      ]);

      if (customerResults && customerResults.length > 0) {
        setCustomerDetailsMap((prev) => {
          const next = { ...prev };
          customerResults.forEach((item) => {
            if (item && item.data) {
              next[item.id] = item.data;
            }
          });
          return next;
        });
      }

      if (issuerResults && issuerResults.length > 0) {
        setIssuerDetailsMap((prev) => {
          const next = { ...prev };
          issuerResults.forEach((item) => {
            if (item && item.data) {
              next[item.id] = item.data;
            }
          });
          return next;
        });
      }
    } catch (error) {
      console.error('Error hydrating details for Registro Attività:', error);
    }
  };

  const fetchRows = async () => {
    setLoadingRows(true);
    try {
      const params = {
        limit: rowsPerPage,
        page: page + 1,
        orderBy: 'operation_date',
        order: 'DESC',
      };

      if (filterStatoInizialeId) {
        params.statoInizialeId = Number(filterStatoInizialeId);
      }
      if (filterAttivitaSvoltaId) {
        params.attivitaSvoltaId = Number(filterAttivitaSvoltaId);
      }
      if (filterStatoFinaleId) {
        params.statoFinaleId = Number(filterStatoFinaleId);
      }
      if (isAdmin && filterUserId) {
        params.userId = Number(filterUserId);
      }

      const res = await api.get('/attivita/registro', { params });
      
      // Handle new structure { data: [], total: number } or old array []
      const data = res.data.data || (Array.isArray(res.data) ? res.data : []);
      const total = typeof res.data.total === 'number' ? res.data.total : data.length;

      setRows(data);
      setTotalCount(total);

      if (data.length > 0) {
        hydrateDetails(data);
      }
    } catch (error) {
      console.error('Error fetching Registro Attività:', error);
      toast.error('Errore nel caricamento del registro attività');
    } finally {
      setLoadingRows(false);
    }
  };

  const handleDeleteRow = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      await api.delete(`/attivita/registro/${rowToDelete.id}`);
      toast.success('Attività eliminata');
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      fetchRows();
    } catch (error) {
      console.error('Error deleting Registro Attività entry:', error);
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  };

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    fetchRows();
  }, [filterStatoInizialeId, filterAttivitaSvoltaId, filterStatoFinaleId, filterUserId, isAdmin, page, rowsPerPage]);

  useEffect(() => {
    if (customerOpen) {
      fetchCustomerOptions(customerQuery);
    }
  }, [customerOpen, customerQuery]);

  useEffect(() => {
    if (issuerOpen) {
      fetchIssuerOptions(issuerQuery);
    }
  }, [issuerOpen, issuerQuery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!operationDate || !selectedCustomer || !selectedIssuer || !statoInizialeId || !attivitaSvoltaId || !statoFinaleId) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    const normalizedDate = normalizeDateTimeLocal(operationDate);
    if (!normalizedDate) {
      toast.error('Data operazione non valida');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/attivita/registro', {
        operation_date: normalizedDate,
        customer_id: selectedCustomer.id,
        issuer_id: selectedIssuer.id,
        stato_iniziale_id: Number(statoInizialeId),
        attivita_svolta_id: Number(attivitaSvoltaId),
        stato_finale_id: Number(statoFinaleId),
        nota: nota || undefined,
      });
      toast.success('Attività registrata');
      setNota('');
      setStatoInizialeId('');
      setAttivitaSvoltaId('');
      setStatoFinaleId('');
      setSelectedCustomer(null);
      setSelectedIssuer(null);
      if (autoUpdateNow) {
        setOperationDate(currentInputDate());
      }
      fetchRows();
    } catch (error) {
      console.error('Error creating Registro Attività entry:', error);
      toast.error('Errore nel salvataggio dell\'attività');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderCustomerLabel = (option) => {
    if (!option) return '';
    const pieces = [];
    if (option.ragione_sociale) {
      pieces.push(option.ragione_sociale);
    } else if (option.name || option.surname) {
      pieces.push([option.name, option.surname].filter(Boolean).join(' '));
    }
    if (option.email) {
      pieces.push(option.email);
    }
    if (option.telephone) {
      pieces.push(option.telephone);
    }
    return pieces.join(' – ') || `ID: ${option.id}`;
  };

  const renderIssuerLabel = (option) => {
    if (!option) return '';
    const pieces = [];
    if (option.name) {
      pieces.push(option.name);
    }
    if (option.direzione_generale) {
      pieces.push(option.direzione_generale);
    }
    if (option.pec) {
      pieces.push(option.pec);
    }
    return pieces.join(' – ') || `ID: ${option.id}`;
  };

  const formatIsoDateToIt = (value) => {
    if (!value || typeof value !== 'string') return '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <RegistroLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Registro Attività
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inserimento e consultazione delle attività giornaliere sulle pratiche
            </Typography>
          </Box>
        </Box>
      </Box>

      <StyledCard sx={{ mb: 3 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Data operazione"
                type="datetime-local"
                value={operationDate}
                onChange={(e) => { setOperationDate(e.target.value); setAutoUpdateNow(false); }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <CalendarIcon size={18} />
                    </Box>
                  ),
                }}
              />
              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={autoUpdateNow}
                      onChange={(e) => setAutoUpdateNow(e.target.checked)}
                    />
                  }
                  label="Aggiorna automaticamente"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                open={customerOpen}
                onOpen={() => setCustomerOpen(true)}
                onClose={() => setCustomerOpen(false)}
                options={customerOptions}
                loading={loadingCustomer}
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setCustomerQuery(newInputValue || '');
                }}
                getOptionLabel={(option) => renderCustomerLabel(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <UserIcon size={18} />
                        </Box>
                      ),
                      endAdornment: (
                        <>
                          {loadingCustomer ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                open={issuerOpen}
                onOpen={() => setIssuerOpen(true)}
                onClose={() => setIssuerOpen(false)}
                options={issuerOptions}
                loading={loadingIssuer}
                value={selectedIssuer}
                onChange={(event, newValue) => {
                  setSelectedIssuer(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setIssuerQuery(newInputValue || '');
                }}
                getOptionLabel={(option) => renderIssuerLabel(option)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Creditore"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                          <Banknote size={18} />
                        </Box>
                      ),
                      endAdornment: (
                        <>
                          {loadingIssuer ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Stato iniziale"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                value={statoInizialeId}
                onChange={(e) => setStatoInizialeId(e.target.value)}
              >
                <option value="">Seleziona...</option>
                {statoInizialeList.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                fullWidth
                options={attivitaSvoltaList}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                value={attivitaSvoltaList.find(a => String(a.id) === String(attivitaSvoltaId)) || null}
                onChange={(e, v) => setAttivitaSvoltaId(v ? String(v.id) : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Attività svolta"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Cerca attività..."
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Stato finale"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true }}
                value={statoFinaleId}
                onChange={(e) => setStatoFinaleId(e.target.value)}
              >
                <option value="">Seleziona...</option>
                {statoFinaleList.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Nota sintetica"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1, flexWrap: 'nowrap' }}>
              {isAdmin && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleOpenReportDialog}
                  disabled={generatingReport}
                  startIcon={<FileDown size={16} />}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Report PDF
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {submitting ? 'Salvataggio...' : 'Registra attività'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </StyledCard>

      <StyledCard>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Ultime attività registrate
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {isAdmin && (
              <Autocomplete
                size="small"
                options={userOptions}
                loading={loadingUsers}
                onOpen={() => fetchUserOptions('')}
                onInputChange={(e, v) => fetchUserOptions(v || '')}
                getOptionLabel={(option) => {
                  const fullName = [option.name, option.surname].filter(Boolean).join(' ');
                  return fullName || option.email || `ID: ${option.id}`;
                }}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={userOptions.find(u => String(u.id) === String(filterUserId)) || null}
                onChange={(e, v) => setFilterUserId(v ? String(v.id) : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filtra per utente"
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 260 }}
                    placeholder="Cerca utente..."
                  />
                )}
              />
            )}
            <TextField
              select
              size="small"
              label="Filtra per stato iniziale"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              value={filterStatoInizialeId}
              onChange={(e) => setFilterStatoInizialeId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <option value="">Tutti</option>
              {statoInizialeList.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </TextField>

            <Autocomplete
              size="small"
              options={attivitaSvoltaList}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              value={attivitaSvoltaList.find(a => String(a.id) === String(filterAttivitaSvoltaId)) || null}
              onChange={(e, v) => setFilterAttivitaSvoltaId(v ? String(v.id) : '')}
              sx={{ minWidth: 220 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filtra per attività svolta"
                  InputLabelProps={{ shrink: true }}
                  placeholder="Cerca attività..."
                />
              )}
            />

            <TextField
              select
              size="small"
              label="Filtra per stato finale"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true }}
              value={filterStatoFinaleId}
              onChange={(e) => setFilterStatoFinaleId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <option value="">Tutti</option>
              {statoFinaleList.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </TextField>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Data operazione</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Creditore</TableCell>
                  <TableCell>Stato iniziale</TableCell>
                  <TableCell>Attività svolta</TableCell>
                  <TableCell>Stato finale</TableCell>
                  <TableCell>Nota</TableCell>
                  <TableCell align="center">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingRows ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nessuna attività registrata.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows
                    // No slicing needed here because rows are already paginated by backend
                    .map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{formatDateTime(row.operation_date)}</TableCell>
                        <TableCell>
                          {row.customer_id && customerDetailsMap[row.customer_id]
                            ? renderCustomerLabel(customerDetailsMap[row.customer_id])
                            : row.customer_id
                              ? `ID: ${row.customer_id}`
                              : '-'}
                        </TableCell>
                        <TableCell>
                          {row.issuer_id && issuerDetailsMap[row.issuer_id]
                            ? renderIssuerLabel(issuerDetailsMap[row.issuer_id])
                            : row.issuer_id
                              ? `ID: ${row.issuer_id}`
                              : '-'}
                        </TableCell>
                        <TableCell>
                          {row.stato_iniziale_id && statoInizialeMap[row.stato_iniziale_id] ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                              {statoInizialeMap[row.stato_iniziale_id].color ? (
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statoInizialeMap[row.stato_iniziale_id].color, border: '1px solid rgba(0,0,0,0.2)' }} />
                              ) : null}
                              <span>{statoInizialeMap[row.stato_iniziale_id].name}</span>
                            </Box>
                          ) : (
                            row.stato_iniziale_id || '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {row.attivita_svolta_id && attivitaSvoltaMap[row.attivita_svolta_id]
                            ? attivitaSvoltaMap[row.attivita_svolta_id].name
                            : row.attivita_svolta_id || '-'}
                        </TableCell>
                        <TableCell>
                          {row.stato_finale_id && statoFinaleMap[row.stato_finale_id] ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                              {statoFinaleMap[row.stato_finale_id].color ? (
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: statoFinaleMap[row.stato_finale_id].color, border: '1px solid rgba(0,0,0,0.2)' }} />
                              ) : null}
                              <span>{statoFinaleMap[row.stato_finale_id].name}</span>
                            </Box>
                          ) : (
                            row.stato_finale_id || '-'
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 260,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={row.nota || ''}
                        >
                          {row.nota || '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            {row.customer_id && (
                              <Tooltip title="Apri scheda Cliente">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/customer/${row.customer_id}`)}
                                  color="primary"
                                >
                                  <ExternalLink size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {row.issuer_id && (
                              <Tooltip title="Apri scheda Creditore">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/issuer/${row.issuer_id}`)}
                                  color="secondary"
                                >
                                  <ExternalLink size={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Elimina attività">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteRow(row)}
                                color="error"
                              >
                                <Trash size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Righe per pagina:"
          />
        </Box>
      </StyledCard>

      {isAdmin && (
        <Dialog
          open={reportDialogOpen}
          onClose={handleCloseReportDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Report PDF attività</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                options={userOptions}
                loading={loadingUsers}
                onOpen={() => fetchUserOptions('')}
                onInputChange={(e, v) => fetchUserOptions(v || '')}
                getOptionLabel={(option) => {
                  const fullName = [option.name, option.surname].filter(Boolean).join(' ');
                  return fullName || option.email || `ID: ${option.id}`;
                }}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={userOptions.find(u => String(u.id) === String(reportUserId)) || null}
                onChange={(e, v) => setReportUserId(v ? String(v.id) : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Utente"
                    InputLabelProps={{ shrink: true }}
                    placeholder="Cerca utente..."
                    required
                  />
                )}
              />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap' }}>
                <TextField
                  fullWidth
                  label="Da"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={reportDateFrom}
                  onChange={(e) => setReportDateFrom(e.target.value)}
                />
                <TextField
                  fullWidth
                  label="A"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={reportDateTo}
                  onChange={(e) => setReportDateTo(e.target.value)}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReportDialog}>Annulla</Button>
            <Button
              onClick={handleConfirmGenerateReportPdf}
              variant="contained"
              disabled={generatingReport}
              sx={{ textTransform: 'none' }}
            >
              {generatingReport ? 'Generazione...' : 'Genera PDF'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conferma eliminazione attività</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Stai per eliminare la seguente riga dal registro attività:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">
              <strong>Data operazione:</strong>{' '}
              {rowToDelete ? formatDateTime(rowToDelete.operation_date) : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Cliente:</strong>{' '}
              {rowToDelete && rowToDelete.customer_id
                ? customerDetailsMap[rowToDelete.customer_id]
                  ? renderCustomerLabel(customerDetailsMap[rowToDelete.customer_id])
                  : `ID: ${rowToDelete.customer_id}`
                : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Creditore:</strong>{' '}
              {rowToDelete && rowToDelete.issuer_id
                ? issuerDetailsMap[rowToDelete.issuer_id]
                  ? renderIssuerLabel(issuerDetailsMap[rowToDelete.issuer_id])
                  : `ID: ${rowToDelete.issuer_id}`
                : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Attività svolta:</strong>{' '}
              {rowToDelete && rowToDelete.attivita_svolta_id
                ? attivitaSvoltaMap[rowToDelete.attivita_svolta_id]
                  ? attivitaSvoltaMap[rowToDelete.attivita_svolta_id].name
                  : rowToDelete.attivita_svolta_id
                : '-'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Procedo con l&apos;eliminazione?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
